import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  Search,
  FilterList,
  Download,
  Visibility,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Analytics,
  FileDownload,
  Refresh,
  Clear,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import samplesService, { Sample, SampleStatus } from '../services/samplesService';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import SupervisorStyleAnalysisView from '../components/SupervisorStyleAnalysisView';

interface CompletedSample extends Sample {
  workflow_duration_days?: number;
  total_laboratories_involved?: number;
  completion_efficiency_score?: number;
  client_name?: string;
  batch_info?: {
    batch_number: string;
    total_samples: number;
    completion_rate: number;
  };
  saa_results?: any[];
  icp_results?: any[];
  au_results?: any[];
  volumetrie_results?: any[];
}

interface BatchGroup {
  batch_number: string;
  client_name: string;
  total_samples: number;
  completion_date: string;
  avg_duration: number;
  samples: CompletedSample[];
  laboratories_used: string[];
  avg_efficiency: number;
}

interface FilterState {
  search: string;
  dateRange: {
    start: string;
    end: string;
  };
  client: string;
  laboratory: string;
  method: string;
  priority: string;
  batchNumber: string;
}

const INITIAL_FILTERS: FilterState = {
  search: '',
  dateRange: { start: '', end: '' },
  client: '',
  laboratory: '',
  method: '',
  priority: '',
  batchNumber: '',
};

const CompletedSamplesAdmin: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();

  // State management
  const [batchGroups, setBatchGroups] = useState<BatchGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);
  const [selectedBatch, setSelectedBatch] = useState<BatchGroup | null>(null);
  const [selectedSample, setSelectedSample] = useState<CompletedSample | null>(null);
  const [batchDetailDialog, setBatchDetailDialog] = useState(false);
  const [sampleDetailDialog, setSampleDetailDialog] = useState(false);
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  const [exportLoading, setExportLoading] = useState(false);
  
  // Performance optimization - debounced search
  const [searchDebounce, setSearchDebounce] = useState<NodeJS.Timeout | null>(null);

  // Statistics for the dashboard header
  const [statistics, setStatistics] = useState({
    total_completed: 0,
    completed_this_month: 0,
    avg_turnaround_days: 0,
    top_performing_lab: '',
    total_batches: 0,
    completion_rate: 0,
  });

  // Extract analysis results from metadata
  const extractAnalysisResults = (sample: Sample, labType: string): any[] => {
    const metadata = sample.metadonnees || {};
    const results: any[] = [];

    // Extract from analysis_form
    const analysisForm = metadata[`${labType}_analysis_form`];
    if (analysisForm && analysisForm.elements) {
      analysisForm.elements.forEach((element: any) => {
        if (element.readings && element.readings.length > 0) {
          element.readings.forEach((reading: any) => {
            results.push({
              element: element.element,
              value: reading.value,
              unit: reading.unit || element.unit || 'ppm',
              detection_limit: reading.detection_limit || element.detection_limit,
              uncertainty: reading.uncertainty,
              dilution_factor: reading.dilution_factor || 1,
              date_analysis: reading.date_analysis || analysisForm.date_analysis,
              method: analysisForm.method || `Méthode ${labType.toUpperCase()}`,
              analyst: analysisForm.analyst || 'N/A',
            });
          });
        }
      });
    }

    // Extract from analysis_data
    const analysisData = metadata[`${labType}_analysis_data`];
    if (analysisData && analysisData.analysis_readings) {
      analysisData.analysis_readings.forEach((reading: any) => {
        results.push({
          element: reading.element,
          value: reading.value,
          unit: reading.unit || 'ppm',
          detection_limit: reading.detection_limit,
          uncertainty: reading.uncertainty,
          dilution_factor: reading.dilution_factor || 1,
          date_analysis: reading.date_analysis || analysisData.date_analysis,
          method: analysisData.method || `Méthode ${labType.toUpperCase()}`,
          analyst: analysisData.analyst || 'N/A',
        });
      });
    }

    return results;
  };

  // Calculate workflow duration in days
  const calculateWorkflowDuration = (sample: Sample): number => {
    if (!sample.date_reception || !sample.date_pret) return 0;
    
    const reception = new Date(sample.date_reception);
    const completion = new Date(sample.date_pret);
    const diffMs = completion.getTime() - reception.getTime();
    return Math.round(diffMs / (1000 * 60 * 60 * 24));
  };

  // Calculate number of laboratories involved
  const calculateLaboratoriesInvolved = (sample: Sample): number => {
    const metadata = sample.metadonnees || {};
    let count = 0;
    
    if (metadata.saa_analysis_form || metadata.saa_analysis_data) count++;
    if (metadata.icp_analysis_form || metadata.icp_analysis_data) count++;
    if (metadata.au_analysis_form || metadata.au_analysis_data) count++;
    if (metadata.volumetrie_analysis_form || metadata.volumetrie_analysis_data) count++;
    
    return count;
  };

  // Calculate efficiency score (0-100)
  const calculateEfficiencyScore = (sample: Sample): number => {
    const duration = calculateWorkflowDuration(sample);
    if (duration === 0) return 0;
    
    // Efficiency based on turnaround time (48h = 100%, longer = lower score)
    const targetDays = 2; // 48 hours
    const efficiency = Math.max(0, Math.min(100, (targetDays / duration) * 100));
    return Math.round(efficiency);
  };

  // Extract batch information
  const extractBatchInfo = (sample: Sample): any => {
    const metadata = sample.metadonnees || {};
    const batchInfo = metadata.batch_info;
    
    if (batchInfo) {
      return {
        batch_number: batchInfo.batch_number || batchInfo.afl_batch_number || sample.sample_id?.split('-').slice(0, -1).join('-') || 'N/A',
        total_samples: batchInfo.total_samples || 1,
        completion_rate: 100,
      };
    }
    
    // Fallback: extract batch number from sample_id (AFL-0048-01 -> AFL-0048)
    const batchFromId = sample.sample_id?.split('-').slice(0, -1).join('-') || 'INCONNU';
    return {
      batch_number: batchFromId,
      total_samples: 1,
      completion_rate: 100,
    };
  };

  // Group samples by batch
  const groupSamplesByBatch = (samples: CompletedSample[]): BatchGroup[] => {
    const batchMap = new Map<string, CompletedSample[]>();

    // Group samples by batch number
    samples.forEach(sample => {
      const batchNumber = sample.batch_info?.batch_number || 'INCONNU';
      if (!batchMap.has(batchNumber)) {
        batchMap.set(batchNumber, []);
      }
      batchMap.get(batchNumber)!.push(sample);
    });

    // Convert to BatchGroup objects
    const batches: BatchGroup[] = [];
    batchMap.forEach((batchSamples, batchNumber) => {
      const firstSample = batchSamples[0];
      const totalDuration = batchSamples.reduce((sum, s) => sum + (s.workflow_duration_days || 0), 0);
      const avgDuration = batchSamples.length > 0 ? totalDuration / batchSamples.length : 0;
      
      const totalEfficiency = batchSamples.reduce((sum, s) => sum + (s.completion_efficiency_score || 0), 0);
      const avgEfficiency = batchSamples.length > 0 ? totalEfficiency / batchSamples.length : 0;

      // Find latest completion date
      const completionDates = batchSamples
        .filter(s => s.date_pret)
        .map(s => new Date(s.date_pret!))
        .sort((a, b) => b.getTime() - a.getTime());
      
      const latestCompletion = completionDates.length > 0 ? completionDates[0] : new Date();

      // Find all laboratories used
      const laboratoriesSet = new Set<string>();
      batchSamples.forEach(sample => {
        if (sample.saa_results && sample.saa_results.length > 0) laboratoriesSet.add('SAA');
        if (sample.icp_results && sample.icp_results.length > 0) laboratoriesSet.add('ICP');
        if (sample.au_results && sample.au_results.length > 0) laboratoriesSet.add('AU');
        if (sample.volumetrie_results && sample.volumetrie_results.length > 0) laboratoriesSet.add('VOLUMÉTRIE');
      });

      batches.push({
        batch_number: batchNumber,
        client_name: firstSample.client_name || 'Client Inconnu',
        total_samples: batchSamples.length,
        completion_date: latestCompletion.toISOString(),
        avg_duration: Math.round(avgDuration * 10) / 10,
        samples: batchSamples.sort((a, b) => a.sample_id.localeCompare(b.sample_id)),
        laboratories_used: Array.from(laboratoriesSet),
        avg_efficiency: Math.round(avgEfficiency),
      });
    });

    // Sort batches by completion date (newest first)
    return batches.sort((a, b) => new Date(b.completion_date).getTime() - new Date(a.completion_date).getTime());
  };

  // Load completed samples grouped by lots with all analysis values
  const loadCompletedSamples = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query filters - get more samples to properly group by batches
      const queryFilters: any = {
        status: [SampleStatus.PRET], // Only completed samples
        page: 1, // Always start from page 1 to group properly
        limit: Math.max(rowsPerPage * 10, 500), // Get more samples for proper batch grouping
      };

      // Apply filters
      if (filters.search.trim()) {
        queryFilters.search = filters.search.trim();
      }

      if (filters.dateRange.start) {
        queryFilters.dateFrom = filters.dateRange.start;
      }
      if (filters.dateRange.end) {
        queryFilters.dateTo = filters.dateRange.end;
      }

      if (filters.client) {
        queryFilters.clientId = filters.client;
      }

      if (filters.laboratory) {
        queryFilters.laboratory = filters.laboratory;
      }

      if (filters.method) {
        queryFilters.method = filters.method;
      }

      if (filters.priority) {
        queryFilters.priority = filters.priority;
      }

      if (filters.batchNumber) {
        queryFilters.batchNumber = filters.batchNumber;
      }

      console.log('🔍 Loading completed samples with filters:', queryFilters);

      const response = await samplesService.getSamples(
        queryFilters.page,
        queryFilters.limit,
        queryFilters
      );

      // Enhance samples with workflow metadata and analysis results
      const enhancedSamples = response.samples.map((sample: Sample) => {
        const enhanced: CompletedSample = {
          ...sample,
          workflow_duration_days: calculateWorkflowDuration(sample),
          total_laboratories_involved: calculateLaboratoriesInvolved(sample),
          completion_efficiency_score: calculateEfficiencyScore(sample),
          client_name: sample.client?.nom || 'Client Inconnu',
          batch_info: extractBatchInfo(sample),
          // Extract analysis results from metadata
          saa_results: extractAnalysisResults(sample, 'saa'),
          icp_results: extractAnalysisResults(sample, 'icp'),
          au_results: extractAnalysisResults(sample, 'au'),
          volumetrie_results: extractAnalysisResults(sample, 'volumetrie'),
        };
        return enhanced;
      });

      // Group samples by batch
      const batches = groupSamplesByBatch(enhancedSamples);
      
      // Apply pagination to batches (not individual samples)
      const startIndex = page * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      const paginatedBatches = batches.slice(startIndex, endIndex);

      setBatchGroups(paginatedBatches);
      setTotalCount(batches.length); // Total number of batches, not samples

      // Update statistics based on all samples
      updateStatistics(enhancedSamples, response.total || 0);

    } catch (error: any) {
      console.error('❌ Error loading completed samples:', error);
      setError('Erreur lors du chargement des échantillons terminés');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  // Update dashboard statistics
  const updateStatistics = (sampleList: CompletedSample[], total: number) => {
    const thisMonth = new Date();
    thisMonth.setDate(1);
    thisMonth.setHours(0, 0, 0, 0);

    const completedThisMonth = sampleList.filter(s => 
      s.date_pret && new Date(s.date_pret) >= thisMonth
    ).length;

    const totalDuration = sampleList.reduce((sum, s) => sum + (s.workflow_duration_days || 0), 0);
    const avgTurnaround = sampleList.length > 0 ? totalDuration / sampleList.length : 0;

    const uniqueBatches = new Set(
      sampleList
        .filter(s => s.batch_info?.batch_number)
        .map(s => s.batch_info!.batch_number)
    ).size;

    setStatistics({
      total_completed: total,
      completed_this_month: completedThisMonth,
      avg_turnaround_days: Math.round(avgTurnaround * 10) / 10,
      top_performing_lab: 'ICP', // Simplified
      total_batches: uniqueBatches,
      completion_rate: Math.round((total / (total + 100)) * 100), // Simplified calculation
    });
  };

  // Debounced search handler
  const handleSearchChange = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    
    // Clear previous timeout
    if (searchDebounce) {
      clearTimeout(searchDebounce);
    }
    
    // Set new timeout for search
    const timeout = setTimeout(() => {
      setPage(0); // Reset to first page
    }, 500); // 500ms debounce
    
    setSearchDebounce(timeout);
  };

  // Filter change handlers
  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0); // Reset to first page when filters change
  };

  const handleDateRangeChange = (type: 'start' | 'end', value: string) => {
    setFilters(prev => ({
      ...prev,
      dateRange: { ...prev.dateRange, [type]: value }
    }));
    setPage(0);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters(INITIAL_FILTERS);
    setPage(0);
  };

  // Export to Excel
  const handleExport = async () => {
    try {
      setExportLoading(true);
      
      // Get all samples matching current filters (not just current page)
      const exportFilters = { ...filters, page: 1, limit: 10000 };
      const response = await samplesService.getSamples(1, 10000, {
        status: [SampleStatus.PRET],
        ...exportFilters
      });

      // Create CSV content
      const csvHeaders = [
        'Lot',
        'ID Échantillon',
        'Client',
        'Date Réception',
        'Date Terminé',
        'Durée (jours)',
        'Laboratoires Impliqués',
        'Score Efficacité',
        'Priorité',
        'Type',
        'Statut',
        'Méthodes Analyses',
        'Nb Résultats SAA',
        'Nb Résultats ICP',
        'Nb Résultats AU',
        'Nb Résultats VOL',
        'Commentaires'
      ];

      const enhancedSamples = response.samples.map((sample: Sample) => ({
        ...sample,
        batch_info: extractBatchInfo(sample),
        saa_results: extractAnalysisResults(sample, 'saa'),
        icp_results: extractAnalysisResults(sample, 'icp'),
        au_results: extractAnalysisResults(sample, 'au'),
        volumetrie_results: extractAnalysisResults(sample, 'volumetrie'),
      }));

      const csvRows = enhancedSamples.map((sample: any) => [
        sample.batch_info?.batch_number || '',
        sample.sample_id,
        sample.client?.nom || '',
        sample.date_reception ? format(parseISO(sample.date_reception), 'dd/MM/yyyy', { locale: fr }) : '',
        sample.date_pret ? format(parseISO(sample.date_pret), 'dd/MM/yyyy', { locale: fr }) : '',
        calculateWorkflowDuration(sample),
        calculateLaboratoriesInvolved(sample),
        calculateEfficiencyScore(sample),
        sample.priorite || '',
        sample.type_echantillon || '',
        sample.statut,
        sample.analyses_demandees?.join(', ') || '',
        sample.saa_results?.length || 0,
        sample.icp_results?.length || 0,
        sample.au_results?.length || 0,
        sample.volumetrie_results?.length || 0,
        sample.commentaires || ''
      ]);

      const csvContent = [csvHeaders, ...csvRows]
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n');

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `echantillons_termines_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      link.click();

      console.log(`✅ Exported ${response.samples.length} completed samples`);
      
    } catch (error) {
      console.error('❌ Export error:', error);
      setError('Erreur lors de l\'export');
    } finally {
      setExportLoading(false);
    }
  };

  // View batch details
  const handleViewBatchDetails = (batch: BatchGroup) => {
    setSelectedBatch(batch);
    setBatchDetailDialog(true);
  };

  // View individual sample details
  const handleViewSampleDetails = (sample: CompletedSample) => {
    setSelectedSample(sample);
    setSampleDetailDialog(true);
  };

  // Toggle batch expansion
  const toggleBatchExpansion = (batchNumber: string) => {
    const newExpanded = new Set(expandedBatches);
    if (newExpanded.has(batchNumber)) {
      newExpanded.delete(batchNumber);
    } else {
      newExpanded.add(batchNumber);
    }
    setExpandedBatches(newExpanded);
  };

  // Load data on component mount and filter changes
  useEffect(() => {
    loadCompletedSamples();
  }, [loadCompletedSamples]);

  // Cleanup search debounce
  useEffect(() => {
    return () => {
      if (searchDebounce) {
        clearTimeout(searchDebounce);
      }
    };
  }, [searchDebounce]);

  // Memoized filter options (for performance with large datasets)
  const filterOptions = useMemo(() => ({
    laboratories: ['LABORATOIRE_SAA', 'LABORATOIRE_ICP', 'LABORATOIRE_AU', 'LABORATOIRE_VOLUMETRIE'],
    priorities: ['NORMALE', 'ELEVEE', 'URGENTE'],
    methods: ['D4A-SAA- PRE.MO/ANA/038', 'D4A-ICP- PRE.MO/ANA/038', 'D4A-AU- PRE.MO/ANA/038'],
  }), []);

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header with Statistics */}
      <Paper sx={{ p: 3, mb: 3, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)` }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle sx={{ color: 'success.main' }} />
              Échantillons Terminés - Groupés par Lot
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Gestion complète des lots terminés avec toutes les valeurs d'analyse et métadonnées workflow
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => loadCompletedSamples()}
              disabled={loading}
            >
              Actualiser
            </Button>
            <Button
              variant="contained"
              startIcon={exportLoading ? <CircularProgress size={20} /> : <FileDownload />}
              onClick={handleExport}
              disabled={exportLoading || loading}
            >
              Exporter Excel
            </Button>
          </Box>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ textAlign: 'center', bgcolor: alpha(theme.palette.success.main, 0.1) }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="h4" color="success.main">
                  {statistics.total_completed.toLocaleString()}
                </Typography>
                <Typography variant="body2">Total Terminés</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ textAlign: 'center', bgcolor: alpha(theme.palette.info.main, 0.1) }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="h4" color="info.main">
                  {statistics.completed_this_month}
                </Typography>
                <Typography variant="body2">Ce Mois</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ textAlign: 'center', bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="h4" color="warning.main">
                  {statistics.avg_turnaround_days}j
                </Typography>
                <Typography variant="body2">TAT Moyen</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ textAlign: 'center', bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="h4" color="primary.main">
                  {statistics.total_batches}
                </Typography>
                <Typography variant="body2">Lots Terminés</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ textAlign: 'center', bgcolor: alpha(theme.palette.secondary.main, 0.1) }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="h4" color="secondary.main">
                  {statistics.top_performing_lab}
                </Typography>
                <Typography variant="body2">Top Lab</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ textAlign: 'center', bgcolor: alpha(theme.palette.success.main, 0.1) }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="h4" color="success.main">
                  {statistics.completion_rate}%
                </Typography>
                <Typography variant="body2">Taux Réussite</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Advanced Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterList />
          Filtres Avancés
        </Typography>
        
        <Grid container spacing={2}>
          {/* Search */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Recherche"
              value={filters.search}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              placeholder="ID, client, lot..."
            />
          </Grid>

          {/* Date Range */}
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              label="Date Début"
              value={filters.dateRange.start}
              onChange={(e) => handleDateRangeChange('start', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              label="Date Fin"
              value={filters.dateRange.end}
              onChange={(e) => handleDateRangeChange('end', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Laboratory Filter */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Laboratoire</InputLabel>
              <Select
                value={filters.laboratory}
                label="Laboratoire"
                onChange={(e) => handleFilterChange('laboratory', e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                {filterOptions.laboratories.map(lab => (
                  <MenuItem key={lab} value={lab}>
                    {lab.replace('LABORATOIRE_', '')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Priority Filter */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Priorité</InputLabel>
              <Select
                value={filters.priority}
                label="Priorité"
                onChange={(e) => handleFilterChange('priority', e.target.value)}
              >
                <MenuItem value="">Toutes</MenuItem>
                {filterOptions.priorities.map(priority => (
                  <MenuItem key={priority} value={priority}>
                    {priority}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Clear Filters */}
          <Grid item xs={12} md={1}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Clear />}
              onClick={clearAllFilters}
              sx={{ height: '56px' }}
            >
              Effacer
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Results Table - Grouped by Batch */}
      <Paper>
        {loading && <LinearProgress />}
        
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="50"></TableCell>
                <TableCell>Numéro de Lot</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Échantillons</TableCell>
                <TableCell>Date Terminé</TableCell>
                <TableCell>Durée Moyenne</TableCell>
                <TableCell>Laboratoires</TableCell>
                <TableCell>Efficacité</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batchGroups.map((batch) => (
                <React.Fragment key={batch.batch_number}>
                  {/* Batch Header Row */}
                  <TableRow hover sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleBatchExpansion(batch.batch_number)}
                      >
                        {expandedBatches.has(batch.batch_number) ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold" color="primary">
                        {batch.batch_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {batch.client_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${batch.total_samples} échantillons`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(parseISO(batch.completion_date), 'dd/MM/yyyy HH:mm', { locale: fr })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${batch.avg_duration}j`}
                        size="small"
                        color={
                          batch.avg_duration <= 2 ? 'success' :
                          batch.avg_duration <= 5 ? 'warning' : 'error'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {batch.laboratories_used.map(lab => (
                          <Chip
                            key={lab}
                            label={lab}
                            size="small"
                            variant="outlined"
                            color="secondary"
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={batch.avg_efficiency}
                          sx={{ width: 60, height: 6, borderRadius: 3 }}
                          color={
                            batch.avg_efficiency >= 80 ? 'success' :
                            batch.avg_efficiency >= 60 ? 'warning' : 'error'
                          }
                        />
                        <Typography variant="caption">
                          {batch.avg_efficiency}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Voir historique complet du lot avec toutes les valeurs d'analyse">
                        <IconButton
                          size="small"
                          onClick={() => handleViewBatchDetails(batch)}
                          color="primary"
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Sample Rows */}
                  {expandedBatches.has(batch.batch_number) && (
                    <>
                      {/* Sample Table Header */}
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.grey[100], 0.5) }}>
                        <TableCell sx={{ paddingLeft: 6 }}></TableCell>
                        <TableCell><Typography variant="caption" fontWeight="bold">ID Échantillon</Typography></TableCell>
                        <TableCell><Typography variant="caption" fontWeight="bold">Type</Typography></TableCell>
                        <TableCell><Typography variant="caption" fontWeight="bold">Priorité</Typography></TableCell>
                        <TableCell><Typography variant="caption" fontWeight="bold">Durée</Typography></TableCell>
                        <TableCell><Typography variant="caption" fontWeight="bold">Analyses</Typography></TableCell>
                        <TableCell><Typography variant="caption" fontWeight="bold">Résultats</Typography></TableCell>
                        <TableCell><Typography variant="caption" fontWeight="bold">Actions</Typography></TableCell>
                      </TableRow>

                      {/* Individual Sample Rows */}
                      {batch.samples.map((sample) => (
                        <TableRow key={sample.id} sx={{ backgroundColor: alpha(theme.palette.grey[50], 0.3) }}>
                          <TableCell sx={{ paddingLeft: 6 }}></TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {sample.sample_id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {sample.type_echantillon || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={sample.priorite || 'NORMALE'}
                              size="small"
                              color={
                                sample.priorite === 'URGENTE' ? 'error' :
                                sample.priorite === 'ELEVEE' ? 'warning' : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {sample.workflow_duration_days}j
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {sample.saa_results && sample.saa_results.length > 0 && (
                                <Chip label="SAA" size="small" color="info" variant="outlined" />
                              )}
                              {sample.icp_results && sample.icp_results.length > 0 && (
                                <Chip label="ICP" size="small" color="success" variant="outlined" />
                              )}
                              {sample.au_results && sample.au_results.length > 0 && (
                                <Chip label="AU" size="small" color="warning" variant="outlined" />
                              )}
                              {sample.volumetrie_results && sample.volumetrie_results.length > 0 && (
                                <Chip label="VOL" size="small" color="secondary" variant="outlined" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {[
                                ...(sample.saa_results || []),
                                ...(sample.icp_results || []),
                                ...(sample.au_results || []),
                                ...(sample.volumetrie_results || [])
                              ].length} valeurs
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Voir toutes les valeurs d'analyse détaillées">
                              <IconButton
                                size="small"
                                onClick={() => handleViewSampleDetails(sample)}
                                color="primary"
                              >
                                <Analytics />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage="Lots par page:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`} lots`
          }
        />
      </Paper>

      {/* Batch Detail Dialog with Complete Analysis Results */}
      <Dialog
        open={batchDetailDialog}
        onClose={() => setBatchDetailDialog(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5">
            Historique Complet du Lot {selectedBatch?.batch_number}
          </Typography>
          <IconButton onClick={() => setBatchDetailDialog(false)}>
            <Clear />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedBatch && (
            <Grid container spacing={3}>
              {/* Batch Summary */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Résumé du Lot
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="primary">{selectedBatch.total_samples}</Typography>
                          <Typography variant="body2">Échantillons</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="success.main">{selectedBatch.avg_duration}j</Typography>
                          <Typography variant="body2">Durée Moyenne</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="warning.main">{selectedBatch.avg_efficiency}%</Typography>
                          <Typography variant="body2">Efficacité</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="info.main">{selectedBatch.laboratories_used.length}</Typography>
                          <Typography variant="body2">Laboratoires</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Complete Sample Table with Analysis Results */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Historique Complet avec Toutes les Valeurs d'Analyse
                    </Typography>
                    <TableContainer sx={{ maxHeight: 600 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Priorité</TableCell>
                            <TableCell>Réception</TableCell>
                            <TableCell>Terminé</TableCell>
                            <TableCell>Durée</TableCell>
                            <TableCell>SAA</TableCell>
                            <TableCell>ICP</TableCell>
                            <TableCell>AU</TableCell>
                            <TableCell>VOL</TableCell>
                            <TableCell>Total Résultats</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedBatch.samples.map((sample) => (
                            <TableRow key={sample.id} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {sample.sample_id}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {sample.type_echantillon || 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={sample.priorite || 'NORMALE'}
                                  size="small"
                                  color={
                                    sample.priorite === 'URGENTE' ? 'error' :
                                    sample.priorite === 'ELEVEE' ? 'warning' : 'default'
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {sample.date_reception ? format(parseISO(sample.date_reception), 'dd/MM HH:mm', { locale: fr }) : 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {sample.date_pret ? format(parseISO(sample.date_pret), 'dd/MM HH:mm', { locale: fr }) : 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={`${sample.workflow_duration_days}j`}
                                  size="small"
                                  color={
                                    (sample.workflow_duration_days || 0) <= 2 ? 'success' :
                                    (sample.workflow_duration_days || 0) <= 5 ? 'warning' : 'error'
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                {sample.saa_results && sample.saa_results.length > 0 ? (
                                  <Typography variant="caption" color="success.main" fontWeight="bold">
                                    {sample.saa_results.length} valeurs
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" color="text.disabled">
                                    Non effectué
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {sample.icp_results && sample.icp_results.length > 0 ? (
                                  <Typography variant="caption" color="success.main" fontWeight="bold">
                                    {sample.icp_results.length} valeurs
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" color="text.disabled">
                                    Non effectué
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {sample.au_results && sample.au_results.length > 0 ? (
                                  <Typography variant="caption" color="success.main" fontWeight="bold">
                                    {sample.au_results.length} valeurs
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" color="text.disabled">
                                    Non effectué
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {sample.volumetrie_results && sample.volumetrie_results.length > 0 ? (
                                  <Typography variant="caption" color="success.main" fontWeight="bold">
                                    {sample.volumetrie_results.length} valeurs
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" color="text.disabled">
                                    Non effectué
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" fontWeight="bold" color="primary">
                                  {[
                                    ...(sample.saa_results || []),
                                    ...(sample.icp_results || []),
                                    ...(sample.au_results || []),
                                    ...(sample.volumetrie_results || [])
                                  ].length} total
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Tooltip title="Voir toutes les valeurs d'analyse en détail">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewSampleDetails(sample)}
                                    color="primary"
                                  >
                                    <Analytics />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDetailDialog(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Individual Sample Detail Dialog with ALL Analysis Results */}
      <Dialog
        open={sampleDetailDialog}
        onClose={() => setSampleDetailDialog(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Toutes les Valeurs d'Analyse - {selectedSample?.sample_id}
          </Typography>
          <IconButton onClick={() => setSampleDetailDialog(false)}>
            <Clear />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedSample && (
            <Grid container spacing={3}>
              {/* Sample Information */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Informations Échantillon
                    </Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">ID:</Typography>
                        <Typography variant="body2" fontWeight="bold">{selectedSample.sample_id}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Client:</Typography>
                        <Typography variant="body2">{selectedSample.client_name}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Type:</Typography>
                        <Typography variant="body2">{selectedSample.type_echantillon}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Priorité:</Typography>
                        <Chip label={selectedSample.priorite || 'NORMALE'} size="small" />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Durée:</Typography>
                        <Chip label={`${selectedSample.workflow_duration_days}j`} size="small" color="primary" />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Workflow Timeline */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Timeline Workflow
                    </Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Réception:</Typography>
                        <Typography variant="body2">
                          {selectedSample.date_reception ? 
                            format(parseISO(selectedSample.date_reception), 'dd/MM/yyyy HH:mm', { locale: fr }) : 
                            'N/A'
                          }
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Terminé:</Typography>
                        <Typography variant="body2">
                          {selectedSample.date_pret ? 
                            format(parseISO(selectedSample.date_pret), 'dd/MM/yyyy HH:mm', { locale: fr }) : 
                            'N/A'
                          }
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Score Efficacité:</Typography>
                        <Chip 
                          label={`${selectedSample.completion_efficiency_score}%`} 
                          size="small" 
                          color={(selectedSample.completion_efficiency_score || 0) >= 80 ? 'success' : 'warning'}
                        />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Analysis Summary */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Résumé Analyses
                    </Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">SAA:</Typography>
                        <Chip 
                          label={`${(selectedSample.saa_results || []).length} résultats`} 
                          size="small" 
                          color={(selectedSample.saa_results || []).length > 0 ? 'success' : 'default'}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">ICP:</Typography>
                        <Chip 
                          label={`${(selectedSample.icp_results || []).length} résultats`} 
                          size="small" 
                          color={(selectedSample.icp_results || []).length > 0 ? 'success' : 'default'}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">AU:</Typography>
                        <Chip 
                          label={`${(selectedSample.au_results || []).length} résultats`} 
                          size="small" 
                          color={(selectedSample.au_results || []).length > 0 ? 'success' : 'default'}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Volumétrie:</Typography>
                        <Chip 
                          label={`${(selectedSample.volumetrie_results || []).length} résultats`} 
                          size="small" 
                          color={(selectedSample.volumetrie_results || []).length > 0 ? 'success' : 'default'}
                        />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* SAA Results */}
              {selectedSample.saa_results && selectedSample.saa_results.length > 0 && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="SAA" color="info" size="small" />
                        Résultats d'Analyse SAA ({selectedSample.saa_results.length} valeurs)
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Élément</TableCell>
                              <TableCell>Valeur</TableCell>
                              <TableCell>Unité</TableCell>
                              <TableCell>Limite Détection</TableCell>
                              <TableCell>Incertitude</TableCell>
                              <TableCell>Facteur Dilution</TableCell>
                              <TableCell>Date Analyse</TableCell>
                              <TableCell>Analyste</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedSample.saa_results.map((result, index) => (
                              <TableRow key={index}>
                                <TableCell><Typography variant="body2" fontWeight="medium">{result.element}</Typography></TableCell>
                                <TableCell><Typography variant="body2" color="primary">{result.value}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.unit}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.detection_limit || 'N/A'}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.uncertainty || 'N/A'}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.dilution_factor}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.date_analysis ? format(parseISO(result.date_analysis), 'dd/MM/yyyy', { locale: fr }) : 'N/A'}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.analyst}</Typography></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* ICP Results */}
              {selectedSample.icp_results && selectedSample.icp_results.length > 0 && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="ICP" color="success" size="small" />
                        Résultats d'Analyse ICP ({selectedSample.icp_results.length} valeurs)
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Élément</TableCell>
                              <TableCell>Valeur</TableCell>
                              <TableCell>Unité</TableCell>
                              <TableCell>Limite Détection</TableCell>
                              <TableCell>Incertitude</TableCell>
                              <TableCell>Facteur Dilution</TableCell>
                              <TableCell>Date Analyse</TableCell>
                              <TableCell>Analyste</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedSample.icp_results.map((result, index) => (
                              <TableRow key={index}>
                                <TableCell><Typography variant="body2" fontWeight="medium">{result.element}</Typography></TableCell>
                                <TableCell><Typography variant="body2" color="primary">{result.value}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.unit}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.detection_limit || 'N/A'}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.uncertainty || 'N/A'}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.dilution_factor}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.date_analysis ? format(parseISO(result.date_analysis), 'dd/MM/yyyy', { locale: fr }) : 'N/A'}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.analyst}</Typography></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* AU Results */}
              {selectedSample.au_results && selectedSample.au_results.length > 0 && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="AU" color="warning" size="small" />
                        Résultats d'Analyse AU ({selectedSample.au_results.length} valeurs)
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Élément</TableCell>
                              <TableCell>Valeur</TableCell>
                              <TableCell>Unité</TableCell>
                              <TableCell>Limite Détection</TableCell>
                              <TableCell>Incertitude</TableCell>
                              <TableCell>Facteur Dilution</TableCell>
                              <TableCell>Date Analyse</TableCell>
                              <TableCell>Analyste</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedSample.au_results.map((result, index) => (
                              <TableRow key={index}>
                                <TableCell><Typography variant="body2" fontWeight="medium">{result.element}</Typography></TableCell>
                                <TableCell><Typography variant="body2" color="primary">{result.value}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.unit}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.detection_limit || 'N/A'}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.uncertainty || 'N/A'}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.dilution_factor}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.date_analysis ? format(parseISO(result.date_analysis), 'dd/MM/yyyy', { locale: fr }) : 'N/A'}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.analyst}</Typography></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Volumetrie Results */}
              {selectedSample.volumetrie_results && selectedSample.volumetrie_results.length > 0 && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="VOL" color="secondary" size="small" />
                        Résultats d'Analyse Volumétrie ({selectedSample.volumetrie_results.length} valeurs)
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Élément</TableCell>
                              <TableCell>Valeur</TableCell>
                              <TableCell>Unité</TableCell>
                              <TableCell>Limite Détection</TableCell>
                              <TableCell>Incertitude</TableCell>
                              <TableCell>Facteur Dilution</TableCell>
                              <TableCell>Date Analyse</TableCell>
                              <TableCell>Analyste</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedSample.volumetrie_results.map((result, index) => (
                              <TableRow key={index}>
                                <TableCell><Typography variant="body2" fontWeight="medium">{result.element}</Typography></TableCell>
                                <TableCell><Typography variant="body2" color="primary">{result.value}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.unit}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.detection_limit || 'N/A'}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.uncertainty || 'N/A'}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.dilution_factor}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.date_analysis ? format(parseISO(result.date_analysis), 'dd/MM/yyyy', { locale: fr }) : 'N/A'}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{result.analyst}</Typography></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Comments */}
              {selectedSample.commentaires && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Commentaires
                      </Typography>
                      <Typography variant="body2">
                        {selectedSample.commentaires}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSampleDetailDialog(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompletedSamplesAdmin;
